import type {
  Page,
  PageQuery,
  MerchantMeResponseDto,
  MerchantProfileDto,
  MerchantProfileRequestDto,
  MerchantImageUploadRequestDto,
  MerchantImageUploadResponseDto,
  ExternalLinkPlatformOptionDto,
  MerchantDiscoveryOptionsDto,
  MerchantBusinessHoursDto,
  MerchantBusinessHoursRequestDto,
  MerchantVisitIntentReportDto,
  MerchantFeatureStatusDto,
  CategoryDto,
  MerchantDashboardDto,
  MetricsRange,
  BenefitPreviewDto,
  BenefitPreviewRequestDto,
  BenefitTypeDto,
  PromotionDto,
  PromotionRequestDto,
  PromotionRuleTemplateOptionDto,
  PromotionStatus,
  PromotionSummaryDto,
  CouponDto,
  CouponRequestDto,
  CouponStatus,
  CouponSummaryDto,
  MerchantCustomerDto,
  MerchantCustomerSummaryDto,
  ReviewDto,
  ReviewReplyDto,
  ReviewStatusUpdateDto,
  CashbackConfigDto,
  CashbackEntryDto,
  CashbackStatus,
  CashbackSummaryDto,
  MerchantInvoiceDto,
  MerchantInvoiceChargeDto,
  MerchantPlanOptionDto,
  SubscriptionDto,
  PaymentMethod,
  MerchantEmployeeDto,
  MerchantEmployeeRequestDto,
  MerchantEmployeeStatusRequestDto,
  MerchantEmployeeAccessDetailDto,
  MerchantAccessProfileDto,
  MerchantAccessProfileRequestDto,
  MerchantAccessProfilePermissionsUpdateDto,
} from '@kardume/types';
import api from './http';

const toParams = (query: PageQuery = {}) => ({
  page: query.page ?? 0,
  size: query.size ?? 20,
  ...(query.sort ? { sort: query.sort } : {}),
  ...(query.query ? { query: query.query } : {}),
});

/**
 * Admin app API (merchant panel). Every call is implicitly scoped to the
 * authenticated merchant by the backend, via the bearer token.
 *
 * Base: kardume_urbano_core. See docs/endpoints-core.md.
 */
export const adminApi = {
  getMe(): Promise<MerchantMeResponseDto> {
    return api.get<MerchantMeResponseDto>('/v1/merchant/auth/me').then((r) => r.data);
  },

  /**
   * Se a loja está em destaque, em que contexto e até quando (card 262).
   *
   * O painel mostra isso no cabeçalho porque hoje o parceiro não tem como
   * saber: o destaque é criado pelo comercial no backoffice, aparece na
   * vitrine, e a primeira vez que o lojista percebe que existia é quando acaba
   * e o movimento cai - um benefício que só se manifesta como perda.
   *
   * Lista vazia é resposta normal, não erro.
   */
  getMyFeatures(): Promise<MerchantFeatureStatusDto> {
    return api.get<MerchantFeatureStatusDto>('/v1/merchant/features').then((r) => r.data);
  },

  /* Store profile ("Perfil da loja") */
  getProfile(): Promise<MerchantProfileDto> {
    return api.get<MerchantProfileDto>('/v1/merchant/profile').then((r) => r.data);
  },
  updateProfile(payload: MerchantProfileRequestDto): Promise<MerchantProfileDto> {
    return api.put<MerchantProfileDto>('/v1/merchant/profile', payload).then((r) => r.data);
  },
  uploadProfileImage(payload: MerchantImageUploadRequestDto): Promise<MerchantImageUploadResponseDto> {
    return api.post<MerchantImageUploadResponseDto>('/v1/merchant/profile/images', payload).then((r) => r.data);
  },
  /** Catalog of external sales channels - the only source of the platform list. */
  listExternalLinkPlatforms(): Promise<ExternalLinkPlatformOptionDto[]> {
    return api
      .get<ExternalLinkPlatformOptionDto[]>('/v1/merchant/profile/external-link-platforms')
      .then((r) => r.data);
  },
  /* Horário de atendimento ("Horários") - recurso próprio, não parte do perfil:
     é o dado que mais muda, e reenviar o perfil inteiro para corrigir um
     feriado reescreveria imagens, comodidades e cadastro junto. */
  getBusinessHours(): Promise<MerchantBusinessHoursDto> {
    return api.get<MerchantBusinessHoursDto>('/v1/merchant/business-hours').then((r) => r.data);
  },
  updateBusinessHours(payload: MerchantBusinessHoursRequestDto): Promise<MerchantBusinessHoursDto> {
    return api.put<MerchantBusinessHoursDto>('/v1/merchant/business-hours', payload).then((r) => r.data);
  },

  /** Amenities, payment methods and price ranges the profile screen may offer. */
  getDiscoveryOptions(): Promise<MerchantDiscoveryOptionsDto> {
    return api
      .get<MerchantDiscoveryOptionsDto>('/v1/merchant/profile/discovery-options')
      .then((r) => r.data);
  },

  /**
   * Interesse dos clientes na loja (card 214): quantos querem conhecer, quantos
   * a têm como favorita e quantos já foram.
   *
   * A loja sai do token, e não de um id na URL: um `merchantId` no caminho
   * transformaria isto num jeito de ler o interesse acumulado pelo concorrente.
   */
  getVisitIntentInsights(): Promise<MerchantVisitIntentReportDto> {
    return api
      .get<MerchantVisitIntentReportDto>('/v1/merchant/insights/visit-intent')
      .then((r) => r.data);
  },

  /* Public catalog categories (used to pick the store category) */
  listCategories(): Promise<CategoryDto[]> {
    return api.get<CategoryDto[]>('/v1/categories').then((r) => r.data);
  },

  /* Dashboard / metrics */
  getDashboard(range: MetricsRange = '30d'): Promise<MerchantDashboardDto> {
    return api.get<MerchantDashboardDto>('/v1/merchant/dashboard', { params: { range } }).then((r) => r.data);
  },

  /* Promotions / ads */
  listPromotions(query?: PageQuery & { status?: PromotionStatus }): Promise<Page<PromotionDto>> {
    return api
      .get<Page<PromotionDto>>('/v1/merchant/promotions', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  /**
   * Catálogo de cláusulas que o formulário de anúncio oferece.
   *
   * Só as ativas e já na ordem definida pela operação - a tela não reordena
   * nem filtra, senão dois painéis mostrariam a mesma lista em ordens
   * diferentes.
   */
  listPromotionRuleTemplates(): Promise<PromotionRuleTemplateOptionDto[]> {
    return api
      .get<PromotionRuleTemplateOptionDto[]>('/v1/merchant/promotions/rule-templates')
      .then((r) => r.data);
  },
  /**
   * Catálogo de tipos de benefício (card #188).
   *
   * Vem com os metadados de validação de cada tipo - é o que permite abrir um
   * tipo novo no backoffice sem deploy do painel.
   */
  listBenefitTypes(): Promise<BenefitTypeDto[]> {
    return api.get<BenefitTypeDto[]>('/v1/merchant/promotions/benefit-types').then((r) => r.data);
  },
  /**
   * Pré-visualização do benefício. POST porque a configuração tem quinze
   * campos, não porque escreva algo - nada é persistido.
   */
  previewBenefit(payload: BenefitPreviewRequestDto): Promise<BenefitPreviewDto> {
    return api
      .post<BenefitPreviewDto>('/v1/merchant/promotions/benefit-preview', payload)
      .then((r) => r.data);
  },
  getPromotionSummary(): Promise<PromotionSummaryDto> {
    return api.get<PromotionSummaryDto>('/v1/merchant/promotions/summary').then((r) => r.data);
  },
  getPromotion(id: string): Promise<PromotionDto> {
    return api.get<PromotionDto>(`/v1/merchant/promotions/${id}`).then((r) => r.data);
  },
  createPromotion(payload: PromotionRequestDto): Promise<PromotionDto> {
    return api.post<PromotionDto>('/v1/merchant/promotions', payload).then((r) => r.data);
  },
  /**
   * Salva o anúncio inteiro. **Não existe `PUT` parcial aqui.**
   *
   * O `galleryUrls` do corpo substitui o conjunto de fotos, e o core o lê como
   * lista não anulável: campo ausente é lista vazia e APAGA a galeria (spec 034
   * §4, `MerchantPromotionRequestDto`). Por isso `PromotionRequestDto.galleryUrls`
   * é obrigatório no tipo - quem chama daqui não consegue montar um corpo sem
   * ele, nem "salvando só o título".
   */
  updatePromotion(id: string, payload: PromotionRequestDto): Promise<PromotionDto> {
    return api.put<PromotionDto>(`/v1/merchant/promotions/${id}`, payload).then((r) => r.data);
  },
  setPromotionStatus(id: string, action: 'activate' | 'pause'): Promise<PromotionDto> {
    return api.post<PromotionDto>(`/v1/merchant/promotions/${id}/${action}`).then((r) => r.data);
  },
  deletePromotion(id: string): Promise<void> {
    return api.delete(`/v1/merchant/promotions/${id}`).then(() => undefined);
  },

  /* Coupons */
  listCoupons(query?: PageQuery & { status?: CouponStatus }): Promise<Page<CouponDto>> {
    return api
      .get<Page<CouponDto>>('/v1/merchant/coupons', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  getCouponSummary(): Promise<CouponSummaryDto> {
    return api.get<CouponSummaryDto>('/v1/merchant/coupons/summary').then((r) => r.data);
  },
  createCoupon(payload: CouponRequestDto): Promise<CouponDto> {
    return api.post<CouponDto>('/v1/merchant/coupons', payload).then((r) => r.data);
  },
  updateCoupon(id: string, payload: CouponRequestDto): Promise<CouponDto> {
    return api.put<CouponDto>(`/v1/merchant/coupons/${id}`, payload).then((r) => r.data);
  },
  deleteCoupon(id: string): Promise<void> {
    return api.delete(`/v1/merchant/coupons/${id}`).then(() => undefined);
  },

  /* Customers (store's audience) */
  listCustomers(query?: PageQuery): Promise<Page<MerchantCustomerDto>> {
    return api.get<Page<MerchantCustomerDto>>('/v1/merchant/customers', { params: toParams(query) }).then((r) => r.data);
  },
  getCustomerSummary(): Promise<MerchantCustomerSummaryDto> {
    return api.get<MerchantCustomerSummaryDto>('/v1/merchant/customers/summary').then((r) => r.data);
  },

  /* Reviews */
  listReviews(query?: PageQuery): Promise<Page<ReviewDto>> {
    return api.get<Page<ReviewDto>>('/v1/merchant/reviews', { params: toParams(query) }).then((r) => r.data);
  },
  replyReview(id: string, payload: ReviewReplyDto): Promise<ReviewDto> {
    return api.post<ReviewDto>(`/v1/merchant/reviews/${id}/reply`, payload).then((r) => r.data);
  },
  moderateReview(id: string, payload: ReviewStatusUpdateDto): Promise<ReviewDto> {
    return api.patch<ReviewDto>(`/v1/merchant/reviews/${id}/status`, payload).then((r) => r.data);
  },

  /* Cashback */
  getCashbackConfig(): Promise<CashbackConfigDto> {
    return api.get<CashbackConfigDto>('/v1/merchant/cashback/config').then((r) => r.data);
  },
  updateCashbackConfig(payload: CashbackConfigDto): Promise<CashbackConfigDto> {
    return api.put<CashbackConfigDto>('/v1/merchant/cashback/config', payload).then((r) => r.data);
  },
  listCashbackEntries(query?: PageQuery & { status?: CashbackStatus }): Promise<Page<CashbackEntryDto>> {
    return api
      .get<Page<CashbackEntryDto>>('/v1/merchant/cashback/entries', {
        params: { ...toParams(query), ...(query?.status ? { status: query.status } : {}) },
      })
      .then((r) => r.data);
  },
  getCashbackSummary(params?: { query?: string }): Promise<CashbackSummaryDto> {
    return api
      .get<CashbackSummaryDto>('/v1/merchant/cashback/summary', {
        params: { ...(params?.query ? { query: params.query } : {}) },
      })
      .then((r) => r.data);
  },

  /* Billing (autoatendimento do lojista, #127) */
  getSubscription(): Promise<SubscriptionDto> {
    return api.get<SubscriptionDto>('/v1/merchant/billing/subscription').then((r) => r.data);
  },
  listInvoices(query?: PageQuery): Promise<Page<MerchantInvoiceDto>> {
    return api.get<Page<MerchantInvoiceDto>>('/v1/merchant/billing/invoices', { params: toParams(query) }).then((r) => r.data);
  },
  /** Planos ativos oferecidos na troca, com `current: true` no vigente. */
  getBillingPlans(): Promise<MerchantPlanOptionDto[]> {
    return api.get<MerchantPlanOptionDto[]>('/v1/merchant/billing/plans').then((r) => r.data);
  },
  /** Troca o plano da própria assinatura a partir do próximo ciclo. */
  changePlan(planId: string): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>('/v1/merchant/billing/subscription/change-plan', { planId }).then((r) => r.data);
  },
  /** Encerra a própria assinatura (idempotente se já cancelada). */
  cancelSubscription(): Promise<SubscriptionDto> {
    return api.post<SubscriptionDto>('/v1/merchant/billing/subscription/cancel').then((r) => r.data);
  },
  /** Abre a cobrança de uma fatura em aberto; a baixa vem pelo webhook do provedor. */
  payInvoice(invoiceId: string, method: PaymentMethod = 'PIX'): Promise<MerchantInvoiceChargeDto> {
    return api
      .post<MerchantInvoiceChargeDto>(`/v1/merchant/billing/invoices/${invoiceId}/pay`, null, { params: { method } })
      .then((r) => r.data);
  },

  /* Staff / employees (access) - scoped to the authenticated merchant */
  listEmployees(query?: PageQuery): Promise<Page<MerchantEmployeeDto>> {
    return api.get<Page<MerchantEmployeeDto>>('/v1/merchant/employees', { params: toParams(query) }).then((r) => r.data);
  },
  createEmployee(payload: MerchantEmployeeRequestDto): Promise<MerchantEmployeeDto> {
    return api.post<MerchantEmployeeDto>('/v1/merchant/employees', payload).then((r) => r.data);
  },
  updateEmployee(id: string, payload: MerchantEmployeeRequestDto): Promise<MerchantEmployeeDto> {
    return api.put<MerchantEmployeeDto>(`/v1/merchant/employees/${id}`, payload).then((r) => r.data);
  },
  setEmployeeStatus(id: string, payload: MerchantEmployeeStatusRequestDto): Promise<MerchantEmployeeDto> {
    return api.patch<MerchantEmployeeDto>(`/v1/merchant/employees/${id}/status`, payload).then((r) => r.data);
  },
  getEmployeeAccess(employeeId: string): Promise<MerchantEmployeeAccessDetailDto> {
    return api.get<MerchantEmployeeAccessDetailDto>(`/v1/merchant/employees/${employeeId}`).then((r) => r.data);
  },

  /* Access profiles ("perfis de acesso") */
  listPermissions(): Promise<string[]> {
    return api.get<string[]>('/v1/merchant/access/permissions').then((r) => r.data);
  },
  listAccessProfiles(): Promise<MerchantAccessProfileDto[]> {
    return api.get<MerchantAccessProfileDto[]>('/v1/merchant/access/profiles').then((r) => r.data);
  },
  createAccessProfile(payload: MerchantAccessProfileRequestDto): Promise<MerchantAccessProfileDto> {
    return api.post<MerchantAccessProfileDto>('/v1/merchant/access/profiles', payload).then((r) => r.data);
  },
  updateAccessProfilePermissions(
    id: string,
    payload: MerchantAccessProfilePermissionsUpdateDto
  ): Promise<MerchantAccessProfileDto> {
    return api
      .put<MerchantAccessProfileDto>(`/v1/merchant/access/profiles/${id}/permissions`, payload)
      .then((r) => r.data);
  },
};
